import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus } from 'react-icons/fa';
import SelectTextField from '../../../../molecules/SelectTextField';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import { InventoryItem } from '../../types/operations';

interface InventoryItemFormProps {
    items: InventoryItem[];
    onAdd: (item: InventoryItem) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
}

const MIN_ROWS = 5;

const generateId = (): string =>
    crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const legalRegimeOptions = [
    { value: 'AF', label: 'Alienação Fiduciária' },
    { value: 'PENHOR', label: 'Penhor' },
];

const assetTypeOptions = [
    { value: 'GRAIN', label: 'Grão' },
    { value: 'INPUT', label: 'Insumo' },
    { value: 'PROCESSED', label: 'Processado' },
    { value: 'LIVESTOCK', label: 'Pecuária' },
];

const unitOptions = [
    { value: 'TON', label: 'Tonelada' },
    { value: 'BAGS', label: 'Sacas' },
    { value: 'LITERS', label: 'Litros' },
    { value: 'HEAD', label: 'Cabeças' },
    { value: 'KG', label: 'Quilogramas' },
];

const formatCurrency = (val: number): string =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const InventoryItemForm: React.FC<InventoryItemFormProps> = ({ items, onAdd, onRemove, disabled = false }) => {
    const legalRegime = useSignal('');
    const assetType = useSignal('');
    const name = useSignal('');
    const value = useSignal(0);
    const product = useSignal('');
    const quantity = useSignal('');
    const unitOfMeasure = useSignal('');
    const cropYear = useSignal('');
    const storageLocation = useSignal('');
    const description = useSignal('');

    const resetForm = () => {
        legalRegime.value = '';
        assetType.value = '';
        name.value = '';
        value.value = 0;
        product.value = '';
        quantity.value = '';
        unitOfMeasure.value = '';
        cropYear.value = '';
        storageLocation.value = '';
        description.value = '';
    };

    const handleAdd = () => {
        if (!legalRegime.value) { toast.error('Selecione o regime legal'); return; }
        if (!assetType.value) { toast.error('Selecione o tipo de ativo'); return; }
        if (!name.value.trim()) { toast.error('Informe o nome do item'); return; }
        if (value.value <= 0) { toast.error('Informe o valor do item'); return; }

        onAdd({
            collateral_id: generateId(),
            legal_regime: legalRegime.value as InventoryItem['legal_regime'],
            asset_type: assetType.value as InventoryItem['asset_type'],
            name: name.value.trim(),
            value: value.value,
            product: product.value.trim() || undefined,
            quantity: quantity.value ? Number(quantity.value) : undefined,
            unit_of_measure: (unitOfMeasure.value as InventoryItem['unit_of_measure']) || undefined,
            crop_year: cropYear.value.trim() || undefined,
            storage_location: storageLocation.value.trim() || undefined,
            description: description.value.trim() || undefined,
        });
        resetForm();
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - items.length);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                    <SelectTextField label="Regime Legal *" items={legalRegimeOptions} value={legalRegime.value} onChange={(e) => { legalRegime.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <SelectTextField label="Tipo de Ativo *" items={assetTypeOptions} value={assetType.value} onChange={(e) => { assetType.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <TextEdit label="Nome do Item *" value={name.value} onChange={(e) => { name.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <CurrencyInput label="Valor (R$) *" value={value.value} onChange={(v) => { value.value = v ?? 0; }} disabled={disabled} fractionDigits={2} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Produto" value={product.value} onChange={(e) => { product.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Quantidade" type="number" value={quantity.value} onChange={(e) => { quantity.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <SelectTextField label="Unidade" items={unitOptions} value={unitOfMeasure.value} onChange={(e) => { unitOfMeasure.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Safra" value={cropYear.value} onChange={(e) => { cropYear.value = (e.target as HTMLInputElement).value; }} placeholder="24/25" disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Local Armazém" value={storageLocation.value} onChange={(e) => { storageLocation.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button" onClick={handleAdd} disabled={disabled} className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaPlus size={12} /> Adicionar
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Regime</th>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Nome</th>
                            <th className="px-3 py-2">Valor</th>
                            <th className="px-3 py-2">Produto</th>
                            <th className="px-3 py-2">Qtd</th>
                            <th className="px-3 py-2">Safra</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.collateral_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">{item.legal_regime === 'AF' ? 'Alienação Fid.' : 'Penhor'}</td>
                                <td className="px-3 py-2">{assetTypeOptions.find(o => o.value === item.asset_type)?.label || item.asset_type}</td>
                                <td className="px-3 py-2">{item.name}</td>
                                <td className="px-3 py-2">{formatCurrency(item.value)}</td>
                                <td className="px-3 py-2">{item.product || '-'}</td>
                                <td className="px-3 py-2">{item.quantity ?? '-'}</td>
                                <td className="px-3 py-2">{item.crop_year || '-'}</td>
                                <td className="px-3 py-2">
                                    <button type="button" onClick={() => onRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                        <FaTimes size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InventoryItemForm;
